import { useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisVerticalIcon, PrinterIcon } from "@heroicons/react/24/solid";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { downloadFile } from "../app/downloadFile";
import AlertMessage, { AlertLevel } from "./AlertMessage";

interface PrintDownloadMenuProps {
	uniqueKey: string;
	printLabel?: string;
	onPrint?: () => void;
	downloadExcelLink?: string;
	downloadCsvLink?: string;
}

export default function PrintDownloadMenu(props: PrintDownloadMenuProps) {
	const [downloadError, setDownloadError] = useState<string | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);

	async function handleDownload(url: string, filenameFallback: string) {
		setDownloadError(null);
		setIsDownloading(true);
		try {
			const result = await downloadFile(url, filenameFallback);
			if (!result.ok) {
				setDownloadError(result.error ?? "Something went wrong downloading the file.");
			}
		} finally {
			setIsDownloading(false);
		}
	}

	return (
		<>
			<Menu as="div" className="relative inline-block">
				<MenuButton
					className="flex items-center rounded-full text-gray-400 hover:text-gray-600"
					disabled={isDownloading}>
					<span className="sr-only">Open options</span>
					<EllipsisVerticalIcon aria-hidden="true" className="size-8" />
				</MenuButton>

				<MenuItems
					anchor="bottom end"
					className="absolute z-10 pb-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none hover:cursor-pointer">
					{props.onPrint && (
						<MenuItem key={`print-${props.uniqueKey}`}>
							<a
								onClick={props.onPrint}
								className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
								<div className="flex items-center">
									<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
									Print {props.printLabel ?? ""}
								</div>
							</a>
						</MenuItem>
					)}
					{props.downloadExcelLink && (
						<MenuItem key={`download-excel-${props.uniqueKey}`}>
							<button
								type="button"
								onClick={() =>
									handleDownload(
										props.downloadExcelLink!,
										`${props.uniqueKey}.xlsx`
									)
								}
								className="block w-full text-left data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
								<div className="flex items-center">
									<ArrowDownTrayIcon className="size-4 mr-2" aria-hidden="true" />
									Download Excel
								</div>
							</button>
						</MenuItem>
					)}
					{props.downloadCsvLink && (
						<MenuItem key={`download-csv-${props.uniqueKey}`}>
							<button
								type="button"
								onClick={() =>
									handleDownload(props.downloadCsvLink!, `${props.uniqueKey}.csv`)
								}
								className="block w-full text-left data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
								<div className="flex items-center">
									<ArrowDownTrayIcon className="size-4 mr-2" aria-hidden="true" />
									Download CSV
								</div>
							</button>
						</MenuItem>
					)}
				</MenuItems>
			</Menu>
			{downloadError && (
				<AlertMessage
					className="mt-2 text-sm"
					type={AlertLevel.Error}
					message={downloadError}
					showIcon
					onClear={() => setDownloadError(null)}
				/>
			)}
		</>
	);
}
